import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context
import '../../index.css'; // Import the CSS file directly

const EmotionalIntelligence = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'emotionalintelligence'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container emotional-intelligence-page">
      <h1 className="page-title">Emotional Intelligence</h1>

      {/* Include the image */}
      <img
        src="/images/wf-eq.webp"
        alt="Emotional Intelligence"
        className="page-image"
      />

      <p className="page-text">
        Welcome to the Emotional Intelligence section of Wholeness Forward. Emotional intelligence (EI) is the ability to recognize, understand, and manage our own emotions while also being able to recognize, understand, and influence the emotions of others. 
        High EI enhances relationships, fosters collaboration, and improves decision-making.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">The Benefits of Emotional Intelligence</h2>
      <ul className="content-list">
        <li><strong>Improved Relationships:</strong> Deepen connections and build trust.</li>
        <li><strong>Better Communication:</strong> Speak and listen with empathy.</li>
        <li><strong>Increased Self-Awareness:</strong> Recognize and manage emotions effectively.</li>
        <li><strong>Enhanced Leadership:</strong> Inspire and connect with others effectively.</li>
        <li><strong>Improved Mental Health:</strong> Reduce stress and emotional conflicts.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Building Emotional Intelligence: Practical Steps</h2>
      <ul className="content-list">
        <li><strong>Practice Self-Awareness:</strong> Reflect on your emotions and how they influence decisions.</li>
        <li><strong>Improve Emotional Regulation:</strong> Use breathing techniques and mindfulness to manage emotions.</li>
        <li><strong>Enhance Empathy:</strong> Listen with an open mind and acknowledge others’ emotions.</li>
        <li><strong>Develop Social Skills:</strong> Strengthen communication through active listening and positive body language.</li>
        <li><strong>Work on Motivation:</strong> Cultivate a resilient mindset to stay motivated in tough times.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      {loading ? (
        <p className="message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}


      <hr className="page-divider" />

      <h3 className="subheading">Join the Emotional Intelligence Community</h3>
      <p className="page-text">
        Share insights, ask questions, and connect with others on the journey to developing emotional intelligence. 
        Together, we can grow and navigate the complexities of emotions and relationships.
      </p>
      <a href="/forum/Emotional_Intelligence" className="btn">Join the Forum</a>
    </div>
  );
};

export default EmotionalIntelligence;
